import React, { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import styles from './dropdown.module.scss';
import { CaretDownIcon } from 'components/icons';

export interface DropdownOption {
  value: string;
  label: string;
  flag?: string;
}

interface DropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  width?: string | number;
  className?: string;
  style?: React.CSSProperties;
  buttonClassName?: string;
  buttonStyle?: React.CSSProperties;
  listClassName?: string;
  listStyle?: React.CSSProperties;
  disabled?: boolean;
  prefix?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  width,
  className,
  style,
  buttonClassName,
  buttonStyle,
  listClassName,
  listStyle,
  disabled,
  prefix,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  const selectedOption = options.find((opt) => opt.value === value);

  const containerStyle = {
    ...(width ? { width: typeof width === 'number' ? `${width}px` : width } : {}),
    ...style,
  };

  return (
    <div className={cx(styles.dropdownWrapper, className)} ref={dropdownRef} style={containerStyle}>
      <button
        type="button"
        className={cx(styles.selectButton, buttonClassName, {
          [styles.open]: isOpen,
          [styles.disabled]: disabled,
        })}
        style={buttonStyle}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <div className={styles.selectedContent}>
          {selectedOption?.flag && <span className={styles.flag}>{selectedOption.flag}</span>}
          <span className={styles.label}>
            {prefix && <span className={styles.prefix}>{prefix}</span>}
            {selectedOption?.label || 'Select...'}
          </span>
        </div>
        <div className={styles.selectChevron}>
          <CaretDownIcon width={16} height={16} />
        </div>
      </button>

      {isOpen && (
        <div className={cx(styles.optionsList, listClassName)} style={listStyle}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cx(styles.optionItem, { [styles.selected]: value === option.value })}
              onClick={() => handleSelect(option.value)}
            >
              <div className={styles.optionContent}>
                {option.flag && <span className={styles.flag}>{option.flag}</span>}
                <span className={styles.optionLabel}>{option.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
